// Durable offline file/photo upload queue.
//
// Online, an image is uploaded by: (1) asking the API for a short-lived S3
// pre-signed URL + storeId (DOCUMENT_UPLOAD_QUERY), (2) PUTting the binary to
// that URL, (3) recording the storeId via a metadata mutation. None of that
// works offline, and a pre-signed URL can't be fetched ahead of time (it
// expires). So offline we stash the raw blob here and defer the ENTIRE chain to
// reconnect.
//
// Because the real storeId only exists after the upload completes, an offline
// upload is handed a temporary storeId (`pending:<uuid>`) immediately so the
// parent form can still record the photo via the outbox. On reconnect we upload
// the blob, learn the real storeId, and register a temp→real mapping (see
// id-map in outbox.js) that the outbox applies to any queued metadata mutation
// before it's sent.
import localforage from 'localforage'
import { notifyOfflineQueues } from './offline-events'
import { DOCUMENT_UPLOAD_QUERY } from './queries'
import {
  failureKind,
  matchesFeature,
  matchesLocation,
  registerId,
} from './outbox'

const store = localforage.createInstance({
  name: 'bowvue',
  storeName: 'pending_uploads',
  description: 'Binary blobs queued for upload when back online',
})

let seqCounter = 0

function uuid() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

// Pass `locationId` / `feature` to restrict to the uploads tagged for one
// downloaded location of one offline feature (the per-location sync). Untagged
// legacy jobs count as infrared. See outbox.matchesFeature; the location is
// compared as a string for the reason given at outbox.matchesLocation.
export async function allUploads({ locationId, feature } = {}) {
  const items = []
  await store.iterate((value) => {
    items.push(value)
  })
  const filtered = items.filter(
    (i) => matchesLocation(i, locationId) && matchesFeature(i, feature)
  )
  return filtered.sort((a, b) => a.seq - b.seq)
}

export async function initSeq() {
  const items = await allUploads()
  seqCounter = items.reduce((max, i) => Math.max(max, i.seq || 0), 0)
}

// Queue a blob and return the temp storeId the parent should record now. The
// blob itself is stored in IndexedDB (localforage handles binary) so it
// survives reloads.
export async function enqueueUpload({
  blob,
  filename,
  contentType,
  locationId,
  feature,
}) {
  const id = uuid()
  seqCounter += 1
  const tempStoreId = 'pending:' + id
  const job = {
    id,
    seq: seqCounter,
    blob,
    filename: filename || 'upload',
    contentType:
      contentType || (blob && blob.type) || 'application/octet-stream',
    tempStoreId,
    // Which downloaded location this photo belongs to; null for untagged jobs.
    locationId: locationId == null ? null : locationId,
    // Which offline feature queued it; null = legacy job, treated as infrared.
    feature: feature == null ? null : feature,
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
    error: null,
  }
  await store.setItem(id, job)
  notifyOfflineQueues()
  return { id, tempStoreId }
}

export async function removeUpload(id) {
  await store.removeItem(id)
  notifyOfflineQueues()
}

// Drop the queued uploads known by these temp storeIds. Used when the queued
// mutation that referenced them is discarded, so the blobs don't upload for a
// photo record nothing will create.
export async function removeByTempIds(tempStoreIds) {
  if (!tempStoreIds || !tempStoreIds.length) return 0
  const wanted = new Set(tempStoreIds)
  const jobs = await allUploads()
  let removed = 0
  for (const job of jobs) {
    if (wanted.has(job.tempStoreId)) {
      await store.removeItem(job.id)
      removed += 1
    }
  }
  if (removed) notifyOfflineQueues()
  return removed
}

// Drop every queued photo one feature holds for one location (removing a
// download). Unscoped is refused, as in outbox.removeForLocation.
export async function removeUploadsForLocation({ locationId, feature } = {}) {
  if (locationId == null) return 0
  const jobs = await allUploads({ locationId, feature })
  for (const job of jobs) {
    await store.removeItem(job.id)
  }
  if (jobs.length) notifyOfflineQueues()
  return jobs.length
}

export async function countUploads() {
  return store.length()
}

// How long an upload may go without sending a byte before it is given up on.
// Generous, because a field photo is several megabytes on a weak signal and
// progress events arrive in bursts; the point is that a STALLED upload ends,
// rather than leaving Sync spinning for as long as the page stays open.
export const UPLOAD_STALL_MS = 60000

// PUT one blob to its pre-signed URL, reporting progress (0..1) as it goes.
//
// XMLHttpRequest, not fetch: the online uploader (FileImageUpload, through
// Dropzone) PUTs with XHR, which is the path proven against this bucket's
// CORS, and only XHR reports upload progress, which is what lets the screen
// say "photo 2 of 5, 40%" instead of nothing at all for a minute. A stall
// (no progress for UPLOAD_STALL_MS) aborts with kind 'stalled'; no response at
// all rejects with kind 'offline'; an HTTP error carries its status.
export function putBlob(
  url,
  blob,
  contentType,
  { onProgress, stallMs = UPLOAD_STALL_MS } = {}
) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let timer = null
    let settled = false
    const settle = (fn, value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      fn(value)
    }
    const fail = (kind, message, status) => {
      const err = new Error(message)
      if (kind) err.kind = kind
      if (status) err.status = status
      settle(reject, err)
    }
    const arm = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        fail('stalled', 'The photo upload stalled')
        try {
          xhr.abort()
        } catch (e) {
          // Already finished; nothing to stop.
        }
      }, stallMs)
    }

    xhr.open('PUT', url)
    if (contentType) xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (event) => {
      arm()
      if (onProgress && event.lengthComputable && event.total) {
        onProgress(event.loaded / event.total)
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(1)
        settle(resolve)
        return
      }
      fail(null, 'S3 PUT failed with status ' + xhr.status, xhr.status)
    }
    xhr.onerror = () => fail('offline', 'Load failed')
    xhr.onabort = () => fail('offline', 'The photo upload was interrupted')
    arm()
    xhr.send(blob)
  })
}

// Replay queued uploads FIFO. For each: fetch a fresh pre-signed URL, PUT the
// blob, then register temp→real storeId so the outbox can patch the matching
// metadata mutation. Must run BEFORE the outbox flush on reconnect so the
// mapping exists when the metadata mutations replay.
//
//   onProgress({ done, total, fraction })  after each step of the way:
//     `done` of `total` photos finished, `fraction` of the current one sent.
//   onUploaded(job, realStoreId)  once a photo is safely uploaded and before
//     its blob leaves the queue, so a feature can keep a copy for display.
//
// A stop that is not this photo's fault (offline, stalled, auth, server) ends
// the flush with everything still queued; only a refusal parks a photo as
// failed.
export async function flushUploads(
  client,
  { onChange, onProgress, onUploaded, locationId, feature } = {}
) {
  const items = await allUploads({ locationId, feature })
  const result = {
    uploaded: 0,
    failed: 0,
    stoppedOffline: false,
    stoppedReason: null,
  }
  const sendable = items.filter((job) => job.status !== 'failed')
  const total = sendable.length
  let done = 0
  const report = (fraction) => {
    if (onProgress && total) onProgress({ done, total, fraction })
  }
  report(0)
  for (const job of sendable) {
    try {
      const { data } = await client.query({
        query: DOCUMENT_UPLOAD_QUERY,
        fetchPolicy: 'no-cache',
      })
      const url = data.uploadLinkDocument.url
      const realStoreId = data.uploadLinkDocument.storeId

      await putBlob(url, job.blob, job.contentType, {
        onProgress: (fraction) => report(fraction),
      })

      // Tell the outbox: wherever a queued mutation referenced this temp id,
      // substitute the real storeId before sending.
      await registerId(job.tempStoreId, realStoreId)
      if (onUploaded) {
        try {
          await onUploaded(job, realStoreId)
        } catch (err) {
          // Keeping a display copy is a nicety; the upload itself succeeded.
        }
      }
      await removeUpload(job.id)
      result.uploaded += 1
      done += 1
      report(0)
    } catch (err) {
      const kind = failureKind(err)
      if (kind !== 'rejected') {
        result.stoppedOffline = true
        result.stoppedReason = kind
        break
      }
      job.attempts += 1
      job.status = 'failed'
      job.error = String(err && err.message ? err.message : err)
      await store.setItem(job.id, job)
      result.failed += 1
    }
    if (onChange) await onChange()
  }
  return result
}

// Reset previously-failed uploads to pending so the next flush retries them
// (a per-location Sync is an explicit human retry). Scoped when locationId is
// given.
export async function resetFailed({ locationId, feature } = {}) {
  const items = await allUploads({ locationId, feature })
  for (const job of items) {
    if (job.status === 'failed') {
      job.status = 'pending'
      job.error = null
      await store.setItem(job.id, job)
    }
  }
}

export async function clearFailed() {
  const items = await allUploads()
  for (const job of items) {
    if (job.status === 'failed') await store.removeItem(job.id)
  }
}

export async function clear() {
  await store.clear()
  seqCounter = 0
}
